import { Col, Row, Tag } from 'antd';
import React, { ReactElement, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormSelect, FormTextInput } from '../../../../../../../shared/components/crmFormElement/FormInput';
import FormTextArea from '../../../../../../../shared/components/crmFormElement/FormTextArea/FormTextArea';
import { GUTTOR_SPACE } from '../../../../../../../shared/constants/commonConstants';
import { RootState } from '../../../../../../../store';
import moment from 'moment';
import { intersectionWith, isEmpty } from 'lodash';
import { useForceUpdate } from '../../../../../../../shared/hooks/useForceUpdate';
import { useSetFormControlConfig } from '../../../../../../../shared/hooks/useSetFormControlConfig';
import { customGTMEvent, getIsPageNameDetail, getIsPageNameNewAdmission, handleUserFocus, monthValidation, yearValidation } from '../../../../service/crmUtils';
import { useHistory, withRouter } from 'react-router-dom';
import PanelCollapse from '../../../../../../../shared/components/collapse/PanelCollapse';
import { setSinceYearDate } from '../../../../../../../store/actions/claimDetailsAction';
import { setAilmentValue, setMappedAilmentValues, setSelectedAilmentValues } from '../../../../../../../store/actions/newAdmissionAction';
import { formInputStates } from '../../../../../../../shared/constants/crmConstants';
import { HISPastAilmentItem } from '../../../../../../../shared/interfaces/newAdmissionInterface';

const PastHistoryCard = ({ isLoading, form, location: { pathname } }: any): ReactElement => {
  const [claimDetailBackup, setClaimDetailBackup] = useState<any>({});
  const dispatch = useDispatch();
  const pastHistory = useSelector((state: RootState): any => state.lookUp.pastHistory);
  const [ailmentDetail, setAilmentDetail] = useState([] as any);
  const { admissionDetails: { claimDetails: claimDetail }, payerLookUp = {},
    payerList: { payer: { uniqueIdentifier } } } = useSelector((state: RootState): RootState => state);
  const { selectedPatientDetail } = useSelector((state: RootState): any => state.hospital);
  const selectedAilmentFromRedux = useSelector((state: RootState): any => state?.newAdmission?.admissionFormVal?.treatmentDetail?.ailment);
  const history = useHistory();
  const {
    ailment, otherAilment, pastHistory: pastHistoryDetail = [],
  } = claimDetail;
  const { selectedDraft, isDraftSelected, ailmentValue, selectedAilmentValues } =
    useSelector((state: RootState): any => state.newAdmission);
  const formControlConfig = useSelector((state: RootState): any => state?.payerList?.formControlConfig || {});
  const pastHistoryFromPayerLookUp = payerLookUp[uniqueIdentifier]?.pastHistory || [];
  const [inputStates, setInputStates] = useState(formInputStates.pastHistoryForm);
  const treatmentPastHistory = selectedPatientDetail?.treatmentPastHistory;
  const commonDetail = { isLoading, form, claimDetailBackup };
  const isDraftLoadedRef = useRef(false);
  const isNewAdmissionPage = getIsPageNameNewAdmission(pathname);
  const forceUpdate = useForceUpdate();
  const getValueBasedOnConfig = useSetFormControlConfig();
  const [updatedHISAilments, setUpdatedHISAilments] = useState<HISPastAilmentItem[]>([]);

  useEffect((): any => {
    if (claimDetail && Object.keys(claimDetail)?.length) {
      setAilmentDetail(pastHistoryDetail);
      const formValues: any = {
        ailment, otherAilment,
      };

      // selectOnChangeHandler(pastHistoryDetail?.map(({value}: any): any => value));
      pastHistoryDetail?.forEach(({ name, sinceYear }: any): any => {
        if (sinceYear !== null) {
          {
            const [year, month] = sinceYear?.split('.');
            formValues[`${name} year`] = year;
            formValues[`${name} month`] = month;
          }
        }
      });
      const updatedFormValues = getValueBasedOnConfig(getIsPageNameDetail(pathname), formValues);
      setClaimDetailBackup(updatedFormValues);
      form.setFieldsValue(updatedFormValues);
      forceUpdate();
    }
  }, [claimDetail]);

  useEffect((): void => {
    if (isDraftSelected && !isDraftLoadedRef.current) {
      const parsedDraftData = selectedDraft?.rawDraft?.length > 0 ? JSON?.parse(selectedDraft?.rawDraft) : {};
      const draftAilmentValues = parsedDraftData?.newFormValue?.treatmentDetail?.ailment?.concat(ailmentValue);
      if (!isEmpty(draftAilmentValues)) {
        // using a ref to track if the draft values have been loaded
        isDraftLoadedRef.current = true;
        selectOnChangeHandler(draftAilmentValues);
      }
    }
  }, [selectedAilmentFromRedux]);

  const getPastHistoryConfig = (): { pastHistoryList: Record<string, any>[], idKey: string } => {
    const isFromPayerLookup: boolean = !isEmpty(pastHistoryFromPayerLookUp);
    const pastHistoryList: Record<string, any>[] = isFromPayerLookup ? pastHistoryFromPayerLookUp : pastHistory;
    const idKey: keyof Record<string, any> = isFromPayerLookup ? 'masterLookId' : 'id';

    return { pastHistoryList, idKey };
  };

  // to prefill pastHistory from HIS
  useEffect((): void => {
    if (!isEmpty(treatmentPastHistory) && !isDraftSelected && isNewAdmissionPage) {
      const pastAilmentsFromHIS = treatmentPastHistory.pastAilments;
      if (isEmpty(pastAilmentsFromHIS)) {
        return;
      }
      const {pastHistoryList, idKey } = getPastHistoryConfig();
      let tempArr: Record<string, any>[] = [];
      let HISAilmentsArr: HISPastAilmentItem[] = [];

      if (typeof pastAilmentsFromHIS === 'string') {
        // Handle string case
        tempArr = pastHistoryList?.filter((item: Record<string, any>): boolean =>
          item?.name === pastAilmentsFromHIS,
        ) || [];
      } else if (Array.isArray(pastAilmentsFromHIS)) {
        // Handle array case
        const mappedAilmentsResult = pastAilmentsFromHIS.reduce((matchedArrays:
          { tempArr: Record<string, any>[], HISAilmentsArr: HISPastAilmentItem[] },
          HISAilmentItem: HISPastAilmentItem): Record<string, any> => {
          const matchedItem = pastHistoryList.find((pastHistoryItem: Record<string, any>): boolean => {
              // First priority: ID match (if HISAilmentItem has an id)
              if (HISAilmentItem.id !== null && HISAilmentItem.id !== undefined) {
                const hisAilmentId = parseInt(String(HISAilmentItem.id), 10);
                const pastHistoryId = pastHistoryItem?.[idKey];
                if (pastHistoryId != null) {
                  return hisAilmentId === Number(pastHistoryId);
                }
              }

              // Fallback: Case insensitive name match
              const hisName = HISAilmentItem.value?.toLowerCase()?.trim();
              const pastHistoryName = pastHistoryItem?.name?.toLowerCase()?.trim();

              return Boolean(hisName && pastHistoryName && hisName === pastHistoryName);
            });

          if (matchedItem) {
            // Add to tempArr (matched items from pastHistoryList)
            matchedArrays.tempArr.push(matchedItem);
            // Add to HISAilmentsArr (id and name from matched item, duration from HIS)
            matchedArrays.HISAilmentsArr.push({
              id: matchedItem[idKey],
              value: matchedItem.name,
              durationYear: HISAilmentItem.durationYear,
              durationMonth: HISAilmentItem.durationMonth,
            });
          }

          return matchedArrays;
        }, { tempArr: [], HISAilmentsArr: [] });
        tempArr = mappedAilmentsResult.tempArr;
        HISAilmentsArr = mappedAilmentsResult.HISAilmentsArr;
      }

      // Common operations after filtering
      if (tempArr && tempArr.length > 0) {
        const HISPastAilment = tempArr.map((item: Record<string, any>): string => String(item?.[idKey]));
        form.setFieldsValue({ ailment: HISPastAilment });
        setAilmentDetail(tempArr);
        setUpdatedHISAilments(HISAilmentsArr);
        dispatch(setAilmentValue(HISPastAilment));
      }
    }
  }, [payerLookUp, treatmentPastHistory]);

  useEffect((): void => {
    if (!isEmpty(updatedHISAilments) && isNewAdmissionPage && !isDraftSelected) {
      updatedHISAilments.forEach((hisAilment: HISPastAilmentItem, index: number): void => {
        const { durationYear, durationMonth } = hisAilment;
        if (typeof durationYear === 'number' && !isNaN(durationYear)) {
          form.setFieldsValue({ [`${hisAilment.value} year`]: durationYear });
          ailmentChangeHandler(durationYear, index, 'year');
          formatValue(durationYear, `${hisAilment.value} year`);
        }
        if (typeof durationMonth === 'number' && !isNaN(durationMonth)) {
          form.setFieldsValue({ [`${hisAilment.value} month`]: durationMonth });
          ailmentChangeHandler(durationMonth, index, 'month');
          formatValue(durationMonth, `${hisAilment.value} month`);
        }
      });
    }
  }, [updatedHISAilments]);

  const selectOnChangeHandler = (selectedAilment: any): void => {
    const { pastHistoryList, idKey } = getPastHistoryConfig();

    const tempArr = intersectionWith(
      pastHistoryList,
      selectedAilment,
      (item: any, ailmentId: any): boolean => parseInt(ailmentId, 10) === item[idKey],
    );

    setAilmentDetail(tempArr);
    dispatch(setAilmentValue(selectedAilment));
  };

  const ailmentChangeHandler = (event: any, index: any, dateType: string): any => {
    if (dateType === 'year') {
      ailmentDetail[index].year = event?.toString().padStart(2, '0');
    } else {
      ailmentDetail[index].month = event?.toString().padStart(2, '0');
    }
    ailmentDetail[index].year = ailmentDetail[index]?.year ? ailmentDetail[index]?.year : '0'; // IPP-3512
    ailmentDetail[index].month = ailmentDetail[index]?.month ? ailmentDetail[index]?.month : '0';
    ailmentDetail[index].sinceYear = `${ailmentDetail[index]?.year}.${ailmentDetail[index].month}`;
    const updatedAilment = [...ailmentDetail];
    setAilmentDetail(updatedAilment);
    dispatch(setSelectedAilmentValues(updatedAilment));
    const resultObject: { [x: number]: any; }[] = [];
    selectedAilmentValues?.forEach((item: any, indexArray: any): any => {
      resultObject.push({ [item.name]: { indexArray, ...item } });
      dispatch(setMappedAilmentValues(resultObject));
    });
    const inputDate = moment().subtract(ailmentDetail[index]?.year, 'year')
      .subtract(ailmentDetail[index].month, 'month').format('MMM YYYY');
    // Split the input date into month and year
    const [monthStr, yearStr] = inputDate?.split(' ');

    // Convert month abbreviation to numerical month
    const monthNumber = new Date(`${monthStr} 1, ${yearStr}`).getMonth() + 1; // Adding 1 because months are 0-indexed

    // Format the month number with leading zero if necessary
    const formattedMonth = monthNumber?.toString().padStart(2, '0');

    // Form the final formatted date
    const formattedDate = `${yearStr}.${formattedMonth}`;
    dispatch(setSinceYearDate(formattedDate));
  };

  const formatValue = (ele: any, key: any): void => {
      let formattedValue;
      // handled 00
      if (ele?.toString() === '00') {
        formattedValue = '0';
      } else if (ele?.toString()?.length === 2 && ele?.toString()?.startsWith('0')) {
        formattedValue = ele; // Keep the value as is (e.g., '08' stays '08')
      } else {
        // to format the value of years and months preceed 0 incase of single digit
        formattedValue = ele?.toString()?.length < 2 && ele !== '0' && ele !== ''
        ? ele?.toString()?.padStart(2, '0')
        : ele?.toString()?.replace(/^0+(?!$)/, '');
      }
      form.setFields([
        {
          name: key,
          value: formattedValue,
        },
      ]);
  };

  const handleYearChange = (event: any, index: any): void => {
    ailmentChangeHandler(event?.target?.value, index, 'year');
  };

  const handleMonthChange = (event: any, index: any): void => {
    ailmentChangeHandler(event?.target?.value, index, 'month');
  };

  const checkRequired = (index: any, dateType: any): boolean => {
    let isRequired = false;
    const month = ailmentDetail?.[index]?.month;
    const year = ailmentDetail?.[index]?.year;

    if (dateType === 'year') {
      isRequired = year == null || year === '0' || year === '00' ? month !== null &&
        month !== '0' && month !== '00' &&
        month !== undefined ? false : true : false;
    } else {
      isRequired = month == null || month === '0' || month === '00' ? year !== null &&
        year !== '0' && year !== '00' && year !== undefined ? false : true : false;
    }
    return isRequired;
  };
  const isDetailPage = getIsPageNameDetail(history.location.pathname);
  const payerConfigValues = Object.values(formControlConfig);
  const max = payerConfigValues?.filter(({ label, MAX }: any): boolean => label === 'Present Complaints' && MAX);
  const maximumValue = (max?.map((ele: any): any => ele?.MAX)?.length > 0) ? max?.map((ele: any): any => ele?.MAX) : false;
  const fieldLength = maximumValue ? max?.map((ele: any): any => ele?.maximumLength) : null;
  const isAlphabetsOnly = payerConfigValues?.filter(({ type, hidden }: any): boolean => type === 'onlyAlpha' && !hidden)?.length;
  const handleExpandCollapseEvent = (isActive: any): void => {
    if (isActive) {
      customGTMEvent({
        event: 'PAST_HISTORY_DETAILS_FORM_EXPAND',
      });
    }
  };

  return (
    <PanelCollapse
      accordion={true}
      onChange={handleExpandCollapseEvent}
      expandIconPosition="right"
      defaultActiveKey={['1']}
      keyName="1"
      header="Past history of chronic illness"
    >
      <Row gutter={GUTTOR_SPACE}>
        <Col md={12}>
          {!isDetailPage && (
            <Row>
              <FormSelect
                detail={{
                  ...commonDetail,
                  key: 'ailment',
                  size: 24,
                  options: pastHistoryFromPayerLookUp?.length > 0 ? pastHistoryFromPayerLookUp : pastHistory.length > 0 ? pastHistory : [],
                  showSearch: true,
                  mode: 'multiple',
                  onChange: selectOnChangeHandler,
                  onlyAlphaNumeric: true,
                  onFocus: (): any => handleUserFocus('ailment', setInputStates, true ),
                  onBlur: (): any => handleUserFocus('ailment', setInputStates, false ),
                  isShowSuggestedValue: inputStates.ailment.isShowSuggestedValue,
                }}
              />
            </Row>
          )}
          <Row gutter={GUTTOR_SPACE}>
            {isDetailPage ?
              <span className="label-title" style={{ marginLeft: '12px' }}>Ailment</span> : <></>
            }
            <Col md={24}>{isDetailPage && ailmentDetail.length === 0 && '-'}</Col>
            {ailmentDetail?.map((ailmentType: any, index: number): ReactElement => {
              return (
                <React.Fragment key={index}>
                  <Col md={12}
                    className={`ailment ${isDetailPage ? 'no-mt' : 'custom-mt'}`}>
                    <Tag className="input-box">
                      <span className="text-elipsis">{ailmentType.name}</span>
                      <small className="label-title mb-0">
                        {(ailmentType?.year && ailmentType?.month) ?
                          moment().subtract(ailmentType.year, 'years').subtract(ailmentType.month, 'months').format('MMM YYYY')
                          : '-'}
                      </small>
                    </Tag>
                  </Col>
                  {!isDetailPage && (
                    <Col md={12} className="ailment-since-year">
                      <span className="label-title">{ailmentType.name} (Enter No. of Years & Months)</span>
                      <Row gutter={32}>
                        <Col md={9} className="px-0 error-max-length-year">
                          <FormTextInput
                            detail={{
                              ...commonDetail,
                              key: `${ailmentType.name} year`,
                              onChange: (event: any): any => {
                                formatValue(event?.target?.value, `${ailmentType.name} year`);
                                handleYearChange(event, index);
                              },
                              onlyNumeric: true,
                              rules: [yearValidation(`${ailmentType?.name} year`, checkRequired(index, 'year'))],
                              hideLabel: true,
                              allowClear: false,
                              isFormDynamic: true,
                              onInitialize: (value: string): any => { value && handleYearChange({ target: { value } }, index); },
                            }}
                          />
                        </Col><span className="label-title label-margin" >years</span>
                        <Col md={9} className="px-0 error-max-length-month">
                          <FormTextInput
                            detail={{
                              ...commonDetail,
                              key: `${ailmentType.name} month`,
                              hideLabel: true,
                              onChange: (event: any): any => {
                                formatValue(event?.target?.value, `${ailmentType.name} month`);
                                handleMonthChange(event, index);
                              },
                              rules: [monthValidation(`${ailmentType?.name} month`, checkRequired(index, 'month'))],
                              onlyNumeric: true,
                              isFormDynamic: true,
                              allowClear: false,
                              onInitialize: (value: string): any => { value && handleMonthChange({ target: { value } }, index); },
                            }}
                          /></Col><span className="label-title label-margin">months</span>
                      </Row>
                    </Col>
                  )}
                </React.Fragment>
              );
            })}
          </Row>
        </Col>
        <Col md={12}>
          <FormTextInput
            detail={{
              ...commonDetail,
              key: 'pastHistoryOfPresentAilment',
              size: 24,
              maxLengthValue: maximumValue,
              fieldLengthValue: fieldLength,
              onlyAlphabet: isAlphabetsOnly > 0 ? true : false, // Restricting Special Characters and Digits
              onFocus: (): void => handleUserFocus('pastHistoryOfPresentAilment', setInputStates, true ),
              onBlur: (): void => handleUserFocus('pastHistoryOfPresentAilment', setInputStates, false ),
              isShowSuggestedValue: inputStates.pastHistoryOfPresentAilment.isShowSuggestedValue,
            }}
          />
          <FormTextArea
            detail={{
              ...commonDetail,
              key: 'otherAilment',
              size: 24,
              labelClass: 'mb-0',
              onFocus: (): any => handleUserFocus('otherAilment', setInputStates, true ),
              onBlur: (): any => handleUserFocus('otherAilment', setInputStates, false ),
              isShowSuggestedValue: inputStates.otherAilment.isShowSuggestedValue,
            }}
          />
        </Col>
      </Row>
    </PanelCollapse>

  );
};

export default withRouter(PastHistoryCard);
